"use client"

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, UserProfile } from '@/lib/api'

/**
 * Маркерный cookie для серверной middleware.
 * Имя отличается от client-web (simple-client-auth), чтобы приложения не конфликтовали
 * при размещении на одном домене.
 */
function setAuthCookie(authenticated: boolean) {
  if (typeof document === 'undefined') return
  if (authenticated) {
    document.cookie = "simple-back-office-auth=true; path=/; max-age=2592000; SameSite=Lax"
  } else {
    document.cookie = "simple-back-office-auth=; path=/; max-age=0"
  }
}

interface AuthState {
  user: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean

  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await api.login({ email, password })
          setAuthCookie(true)
          set({ user: response.user, isAuthenticated: true, isLoading: false })
        } catch (error) {
          setAuthCookie(false)
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        try {
          await api.logout()
        } finally {
          setAuthCookie(false)
          set({ user: null, isAuthenticated: false })
        }
      },

      checkAuth: async () => {
        if (!api.isAuthenticated()) {
          setAuthCookie(false)
          set({ isLoading: false, isAuthenticated: false, user: null })
          return
        }

        try {
          const user = await api.getProfile()
          setAuthCookie(true)
          set({ user, isAuthenticated: true, isLoading: false })
        } catch {
          setAuthCookie(false)
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },
    }),
    {
      name: 'back-office-auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = true
        }
      },
    }
  )
)
